package nl.volleyplanner.web

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.img
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.routing.NavigationCause
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.lib.Dropdown
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import org.w3c.dom.HTMLElement


data class MenuItem(
    val linkText: String,
    val defaultPath: String,
    val authLevel: AuthLevel
) {
    val element = document.create.a(href = defaultPath) {
        classes = setOf("nav-link");
        + linkText
    }

    fun setActive(active: Boolean) {
        if (!active) element.removeClass("active")
        else element.addClass("active")
    }
}

data class Logo(
    val logoPngUrl: String,
    val logoSvgUrl: String?,
    val altText: String
) {
    companion object {
        val LOGO_IRENE = Logo(
            "/img/logo-irene.png",
            "/img/logo-irene.svg",
            "Irene Volleybal"
        )
        val LOGO_VOLLEYPLANNER = Logo(
            "/img/web/logo-volleyplanner.png",
            null,
            "Volleyplanner"
        )

        fun getLogoObj() : Logo {
            return if (AuthContextService.authUser == null || AuthContextService.authUser!!.authLevel == AuthLevel.AnonymousPublic || WebUserAndOrganisationService.selectedOrganisationState.value == null) {
                LOGO_VOLLEYPLANNER
            } else {
                if (WebUserAndOrganisationService.selectedOrganisationState.value!!.organisation.slug == "irene") {
                    // Only show Irene logo for Irene organisation for now
                    // TODO: Make configuration of Logo by organisation possible
                    LOGO_IRENE
                } else {
                    LOGO_VOLLEYPLANNER
                }
            }
        }
    }
}

class HeaderLogoController() : Controller() {
    override val name = "HeaderLogoController"

    override suspend fun render(): HTMLElement {
        return document.create.a (href = "/", classes = "link-body-emphasis text-decoration-none") {
            id = "header-logo-link"

            component(ListeningComponent("HeaderLogoListener",
                listOf(AuthContextService.authUserState, WebUserAndOrganisationService.selectedOrganisationState),
                coroutineScope = CoroutineManager.newServiceScope("HeaderLogoController")
            ) {
                val logoObj = Logo.getLogoObj()
                img (classes = "w-100 object-fit-scale", src = logoObj.logoPngUrl, alt = logoObj.altText) {
                    style = "max-height: 45px;"
                    if (logoObj.logoSvgUrl != null) {
                        attributes["srcset"] = logoObj.logoSvgUrl
                    }
                }
            })
        }
    }
}

class HeaderNavigationController(menuItems: List<RootPageController>) : Controller() {
    override val name = "HeaderNavigationController"

    val menuItems = menuItems.map { rootPage ->
        MenuItem(rootPage.title, rootPage.path, rootPage.requiredAuthLevel)
    }.associateBy { it.defaultPath }

    override suspend fun render(): HTMLElement {
        return document.create.div {
            component(ListeningComponent("HeaderNavigationListener",
                listOf(AuthContextService.authUserState, NavigationManager.state),
                coroutineScope = CoroutineManager.newServiceScope("HeaderNavigationController")
                ) {
                ul(classes = "nav nav-pills justify-content-center") {
                    id = "header-navigation"
                    // For now: only show header menu items for admins who are logged in
                    if (AuthContextService.anonymousToken == null && AuthContextService.authUser?.authLevel == AuthLevel.AdminUser) {
                        menuItems.values
                            .forEach { menuItem ->
                                consumer.li (classes = "nav-item") {
                                    a (href = menuItem.defaultPath, classes = "nav-link") {
                                        if (NavigationManager.state.value.state.path == menuItem.defaultPath) {
                                            classes += "active"
                                            // TODO: active item --> Including subpages
                                        }
                                        +menuItem.linkText
                                    }
                                }
                            }
                    }
                }
            })
        }
    }
}

class UserProfileDropdownController() : Controller() {
    override val name = "UserProfileDropDown"

    fun toggleDropdown() {
        val dropdown = document.getElementById("user-profile-menu-dropdown")!!
        Dropdown.getOrCreateInstance(dropdown).toggle()
    }

    override suspend fun render(): HTMLElement {
        return document.create.div(classes = "nav dropdown justify-content-end col-2") {
            id = "user-profile-dropdown"
            component(ListeningComponent("UserProfileAuthListener",
                listOf(AuthContextService.authUserState, WebUserAndOrganisationService.userProfileState,
                    WebUserAndOrganisationService.availableOrganisationsState, WebUserAndOrganisationService.selectedOrganisationState),
                coroutineScope = CoroutineManager.newServiceScope("UserProfileDropdownController")
            ) {
                val userProfile = WebUserAndOrganisationService.userProfileState.value
                val availableOrganisations = WebUserAndOrganisationService.availableOrganisationsState.value
                val selectedOrganisation = WebUserAndOrganisationService.selectedOrganisationState.value

                div {
                    if (AuthContextService.authUser == null || AuthContextService.authUser!!.authLevel == AuthLevel.AnonymousPublic) {
                        div(classes = "text-end") {
                            a (classes = "btn btn-light") {
                                id = "login-button"
                                href = "/login"
                                +"Inloggen"
                            }
                        }
                    } else if (AuthContextService.authUser?.authLevel == AuthLevel.AdminUser) {
                        if (userProfile == null) {
                            div(classes = "text-end") {
                                a (classes = "btn btn-secondary") {
                                    id = "login-button"
                                    href = "/login"

                                    span {
                                        spinner(small = true)
                                        +"Inloggen"
                                    }
                                }
                            }
                        } else {
                            a(classes = "nav-link dropdown-toggle avatar") {
                                role = "button"
                                id = "user-profile-menu-dropdown"
                                href = "#"
                                //attributes["data-bs-toggle"] = "dropdown"
                                attributes["aria-expanded"] = "false"
                                onClickFunction = { event ->
                                    // Somehow Bootstrap cannot handle the dropdown itself
                                    // Dropdown immediately hides after showing. So need to manually control for now
                                    event.preventDefault()
                                    Dropdown.getOrCreateInstance(event.target as HTMLElement)
                                        .toggle()
                                    event.stopPropagation()
                                }

                                val nameSplit = userProfile.name.split(" ")
                                +"${nameSplit.first().first()}${nameSplit.last().first()}"
                            }

                            ul(classes = "dropdown-menu dropdown-menu-end") {
                                style = "z-index: 2000 !important;"
                                attributes["aria-labelledby"] = "user-profile-menu-dropdown"
                                li {
                                    a(classes = "dropdown-item") {
                                        onClickFunction = { event ->
                                            toggleDropdown()
                                        }
                                        href = "#"
                                        +"Profiel"
                                    }
                                }
                                li {
                                    a(classes = "dropdown-item") {
                                        onClickFunction = { event ->
                                            toggleDropdown()
                                        }
                                        href = "/beheer"
                                        +"Beheer vereniging"
                                    }
                                }
                                li { hr(classes = "dropdown-divider") { } }
                                if ((availableOrganisations?.size ?: 0) > 1) {
                                    availableOrganisations!!.forEach { organisation ->
                                        a(classes = "dropdown-item") {
                                            href = "#"
                                            if (selectedOrganisation?.organisation == organisation) {
                                                classes += "active"
                                                icon("bi-check")
                                            } else {
                                                onClickFunction = { event ->
                                                    toggleDropdown()
                                                    event.preventDefault()
                                                    WebUserAndOrganisationService.changeActiveOrganisation(organisation)
                                                }
                                            }
                                            +organisation.name
                                        }
                                    }
                                    li { hr(classes = "dropdown-divider") { } }
                                }
                                li {
                                    a(classes = "dropdown-item") {
                                        href = "#"
                                        onClickFunction = { event ->
                                            event.preventDefault()
                                            CoroutineManager.DISPATCHER_SCOPE.launch {
                                                Firebase.auth.signOut()
                                                VolleyPlannerApplication.volleyplannerRootApiClient.resetAuth()
                                                NavigationManager.navigateTo("/", NavigationCause.Programmatic)
                                            }
                                        }
                                        +"Uitloggen"
                                    }
                                }
                            }
                        }
                    }
                }
            })
        }
    }
}
